import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Database, Search, RefreshCw, Zap, Settings, Car, 
  ChevronRight, Loader2, CheckCircle, AlertCircle,
  Fuel, Gauge, Cpu, Wrench, DollarSign, TrendingUp,
  Calendar, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTuningData = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectingBrand, setCollectingBrand] = useState(null);
  
  // New state for filtered collection
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [selectedBrandForCollect, setSelectedBrandForCollect] = useState('');
  const [yearFrom, setYearFrom] = useState('2015');
  const [yearTo, setYearTo] = useState(new Date().getFullYear().toString());
  const [collectLimit, setCollectLimit] = useState('20');
  const [collectAllRunning, setCollectAllRunning] = useState(false);
  const [collectAllJobId, setCollectAllJobId] = useState(null);
  
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, entriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/tuning-data/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/tuning-data/?limit=100`, { headers })
      ]);
      
      setStats(statsRes.data);
      setEntries(entriesRes.data.entries || []);
    } catch (error) {
      console.error('Failed to load tuning data:', error);
      toast.error('Fehler beim Laden der Tuningdaten');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/tuning-data/search?q=${encodeURIComponent(searchQuery)}`,
        { headers }
      );
      setEntries(response.data.results || []);
    } catch (error) {
      toast.error('Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };
  
  const openCollectModal = (brand) => {
    setSelectedBrandForCollect(brand);
    setShowCollectModal(true);
  };
  
  const handleCollectForBrand = async () => {
    if (!selectedBrandForCollect) return;
    
    setCollectingBrand(selectedBrandForCollect);
    setShowCollectModal(false);
    
    try {
      const params = new URLSearchParams({
        limit: collectLimit,
        year_from: yearFrom,
        year_to: yearTo
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/tuning-data/collect-brand/${encodeURIComponent(selectedBrandForCollect)}?${params}`,
        {},
        { headers, timeout: 300000 }
      );
      
      const results = response.data.results || {};
      toast.success(
        `${results.collected?.length || 0} von ${results.processed || 0} Fahrzeugen für ${selectedBrandForCollect} gesammelt` +
        (results.failed?.length ? ` (${results.failed.length} Fehler)` : '')
      );
      loadData();
    } catch (error) {
      console.error('Collection error:', error);
      toast.error(`Fehler beim Sammeln für ${selectedBrandForCollect}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setCollectingBrand(null);
    }
  };
  
  const handleCollectAll = async () => {
    setCollectAllRunning(true);
    try {
      const params = new URLSearchParams({
        limit_per_brand: '10',
        year_from: yearFrom,
        year_to: yearTo
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/tuning-data/collect-all?${params}`,
        {},
        { headers }
      );
      
      setCollectAllJobId(response.data.job_id);
      toast.success(`Sammlung für ${response.data.brands?.length || 0} Marken gestartet`);
      
      // Poll for status
      const pollStatus = async () => {
        try {
          const statusRes = await axios.get(
            `${BACKEND_URL}/api/admin/tuning-data/collection-status/${response.data.job_id}`,
            { headers }
          );
          
          if (statusRes.data.status === 'completed') {
            toast.success(`Sammlung abgeschlossen: ${statusRes.data.collected_vehicles} Fahrzeuge gesammelt`);
            setCollectAllRunning(false);
            setCollectAllJobId(null);
            loadData();
          } else {
            toast.info(`Fortschritt: ${statusRes.data.processed_brands}/${statusRes.data.total_brands} Marken, ${statusRes.data.collected_vehicles} Fahrzeuge`);
            setTimeout(pollStatus, 10000);
          }
        } catch (e) {
          console.error('Status poll error:', e);
        }
      };
      
      setTimeout(pollStatus, 10000);
      
    } catch (error) {
      console.error('Collect all error:', error);
      toast.error(`Fehler: ${error.response?.data?.detail || error.message}`);
      setCollectAllRunning(false);
    }
  };
  
  const handleCollectSingle = async (brand, model, engine) => {
    setCollecting(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/tuning-data/collect/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(engine)}`,
        {},
        { headers, timeout: 120000 }
      );
      
      toast.success('Tuningdaten erfolgreich gesammelt');
      setSelectedEntry(response.data.data);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Sammeln der Daten');
    } finally {
      setCollecting(false);
    }
  };
  
  const getFuelTypeColor = (fuelType) => {
    switch (fuelType) {
      case 'diesel': return 'bg-amber-500/20 text-amber-300 border-amber-400/30';
      case 'petrol': return 'bg-green-500/20 text-green-300 border-green-400/30';
      case 'hybrid': return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
      case 'electric': return 'bg-purple-500/20 text-purple-300 border-purple-400/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-400/30';
    }
  };
  
  const getFuelTypeLabel = (fuelType) => {
    switch (fuelType) {
      case 'diesel': return 'Diesel';
      case 'petrol': return 'Benzin';
      case 'hybrid': return 'Hybrid';
      case 'electric': return 'Elektro';
      default: return fuelType;
    }
  };

  // Popular brands for quick collection
  const popularBrands = ['BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen', 'Porsche', 'Opel', 'Ford', 'Toyota'];
  
  if (loading && entries.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F141A] to-[#1A2332] p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F141A] to-[#1A2332] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Database className="w-7 h-7 text-cyan-400" />
              Wissensdatenbank: Tuningdaten
            </h1>
            <p className="text-slate-400 mt-1">
              Technische Tuning-Daten, ECU-Informationen und Preise
            </p>
          </div>
          <Button onClick={loadData} variant="outline" className="border-white/20 text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border-cyan-400/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-cyan-300 text-sm">Gesamt Einträge</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_entries || 0}</p>
                </div>
                <Database className="w-10 h-10 text-cyan-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/20 to-green-600/10 border-green-400/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-300 text-sm">Mit Preisen</p>
                  <p className="text-3xl font-bold text-white">{stats?.with_pricing || 0}</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-400/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-300 text-sm">Mit ECU-Info</p>
                  <p className="text-3xl font-bold text-white">{stats?.with_ecu_info || 0}</p>
                </div>
                <Cpu className="w-10 h-10 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-400/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-300 text-sm">Marken</p>
                  <p className="text-3xl font-bold text-white">{stats?.by_brand?.length || 0}</p>
                </div>
                <Car className="w-10 h-10 text-amber-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Quick Collection */}
        <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Schnelle Datensammlung
              </div>
              <Button
                onClick={handleCollectAll}
                disabled={collectAllRunning}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                size="sm"
              >
                {collectAllRunning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                {collectAllRunning ? 'Läuft...' : 'Alle Marken sammeln'}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-400 text-sm mb-4">
              Klicken Sie auf eine Marke, um Tuningdaten zu sammeln. Ein Dialog ermöglicht die Filterung nach Baujahr.
              <br />
              <span className="text-cyan-400">Alle Marken sammeln</span> startet eine Hintergrund-Sammlung für alle {stats?.by_brand?.length || 35} Marken im Katalog.
            </p>
            <div className="flex flex-wrap gap-2">
              {popularBrands.map((brand) => (
                <Button
                  key={brand}
                  variant="outline"
                  size="sm"
                  onClick={() => openCollectModal(brand)}
                  disabled={collectingBrand === brand}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {collectingBrand === brand ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TrendingUp className="w-4 h-4 mr-2" />
                  )}
                  {brand}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Collection Modal */}
        {showCollectModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-cyan-400/30 w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Filter className="w-5 h-5 text-cyan-400" />
                  Tuningdaten sammeln: {selectedBrandForCollect}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-400 text-sm">
                  Filtern Sie die Fahrzeuge nach Baujahr, um relevante Daten zu sammeln.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-white text-sm mb-1 block">Baujahr von</label>
                    <Input
                      type="number"
                      value={yearFrom}
                      onChange={(e) => setYearFrom(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm mb-1 block">Baujahr bis</label>
                    <Input
                      type="number"
                      value={yearTo}
                      onChange={(e) => setYearTo(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-white text-sm mb-1 block flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    Max. Fahrzeuge
                  </label>
                  <Input
                    type="number"
                    value={collectLimit}
                    onChange={(e) => setCollectLimit(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    min="1"
                    max="100"
                  />
                  <p className="text-slate-500 text-xs mt-1">
                    Höhere Werte = längere Wartezeit, mehr API-Kosten
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCollectModal(false)}
                    className="border-white/20 text-white"
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleCollectForBrand}
                    className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Sammlung starten
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Entry List */}
          <div className="lg:col-span-1">
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Fahrzeuge</CardTitle>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="bg-white/5 border-white/10 text-white"
                  />
                  <Button onClick={handleSearch} size="icon" variant="outline" className="border-white/20">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    onClick={() => setSelectedEntry(entry)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedEntry?.id === entry.id 
                        ? 'bg-cyan-500/20 border border-cyan-400/50' 
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white">{entry.brand} {entry.model}</h4>
                        <p className="text-sm text-slate-400">{entry.engine_name}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs border ${getFuelTypeColor(entry.fuel_type)}`}>
                        {getFuelTypeLabel(entry.fuel_type)}
                      </span>
                      {entry.stage1?.power_ps && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300 border border-green-400/30">
                          +{entry.stage1.gains_ps || Math.round(entry.stage1.power_ps - entry.original?.power_ps)} PS
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                
                {entries.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Tuningdaten vorhanden</p>
                    <p className="text-sm mt-1">Starten Sie die Datensammlung oben</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Detail View */}
          <div className="lg:col-span-2">
            {selectedEntry ? (
              <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-xl">
                        {selectedEntry.brand} {selectedEntry.model}
                      </CardTitle>
                      <p className="text-cyan-400">{selectedEntry.engine_name}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm border ${getFuelTypeColor(selectedEntry.fuel_type)}`}>
                        {getFuelTypeLabel(selectedEntry.fuel_type)}
                      </span>
                      {selectedEntry.engine_code && (
                        <span className="px-3 py-1 rounded-full text-sm bg-slate-500/20 text-slate-300 border border-slate-400/30">
                          {selectedEntry.engine_code}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Power Comparison */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Original */}
                    <div className="p-4 rounded-xl bg-slate-500/10 border border-slate-400/20">
                      <h4 className="text-slate-400 text-sm mb-2">Original</h4>
                      <p className="text-2xl font-bold text-white">
                        {selectedEntry.original?.power_ps || '—'} <span className="text-sm font-normal">PS</span>
                      </p>
                      <p className="text-slate-400">
                        {selectedEntry.original?.torque_nm || '—'} Nm
                      </p>
                    </div>
                    
                    {/* Stage 1 */}
                    <div className="p-4 rounded-xl bg-green-500/10 border border-green-400/30">
                      <h4 className="text-green-400 text-sm mb-2 flex items-center gap-1">
                        <Zap className="w-4 h-4" /> Stage 1
                      </h4>
                      <p className="text-2xl font-bold text-white">
                        {selectedEntry.stage1?.power_ps || '—'} <span className="text-sm font-normal">PS</span>
                      </p>
                      <p className="text-green-400">
                        +{selectedEntry.stage1?.gains_ps || '—'} PS ({selectedEntry.stage1?.gains_percent || '—'}%)
                      </p>
                      <p className="text-slate-400">
                        {selectedEntry.stage1?.torque_nm || '—'} Nm
                      </p>
                      {selectedEntry.stage1?.price_avg && (
                        <p className="text-green-300 mt-2 font-semibold">
                          CHF {selectedEntry.stage1.price_avg}
                        </p>
                      )}
                    </div>
                    
                    {/* Stage 2 */}
                    <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-400/30">
                      <h4 className="text-amber-400 text-sm mb-2 flex items-center gap-1">
                        <Zap className="w-4 h-4" /><Zap className="w-4 h-4 -ml-2" /> Stage 2
                      </h4>
                      <p className="text-2xl font-bold text-white">
                        {selectedEntry.stage2?.power_ps || '—'} <span className="text-sm font-normal">PS</span>
                      </p>
                      <p className="text-amber-400">
                        +{selectedEntry.stage2?.gains_ps || '—'} PS ({selectedEntry.stage2?.gains_percent || '—'}%)
                      </p>
                      <p className="text-slate-400">
                        {selectedEntry.stage2?.torque_nm || '—'} Nm
                      </p>
                      {selectedEntry.stage2?.price_avg && (
                        <p className="text-amber-300 mt-2 font-semibold">
                          CHF {selectedEntry.stage2.price_avg}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* ECU & Hardware */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* ECU Info */}
                    <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-400/30">
                      <h4 className="text-purple-400 text-sm mb-3 flex items-center gap-2">
                        <Cpu className="w-4 h-4" /> ECU Steuergerät
                      </h4>
                      {selectedEntry.ecu?.manufacturer ? (
                        <div className="space-y-1">
                          <p className="text-white font-medium">{selectedEntry.ecu.manufacturer}</p>
                          <p className="text-slate-400">{selectedEntry.ecu.model}</p>
                          {selectedEntry.ecu.generation && (
                            <p className="text-slate-500 text-sm">{selectedEntry.ecu.generation}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-slate-500">Keine ECU-Daten</p>
                      )}
                    </div>
                    
                    {/* Hardware Methods */}
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30">
                      <h4 className="text-blue-400 text-sm mb-3 flex items-center gap-2">
                        <Wrench className="w-4 h-4" /> Hardware & Methoden
                      </h4>
                      {selectedEntry.hardware_methods?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedEntry.hardware_methods.map((hw, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                              <span className="text-white">{hw.tool}</span>
                              <span className="text-slate-400">{hw.method}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500">Keine Hardware-Daten</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Options */}
                  {selectedEntry.options?.length > 0 && (
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <h4 className="text-white text-sm mb-3 flex items-center gap-2">
                        <Settings className="w-4 h-4" /> Verfügbare Optionen
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {selectedEntry.options.filter(o => o.available).map((opt) => (
                          <div 
                            key={opt.id}
                            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between"
                          >
                            <div>
                              <p className="text-white text-sm">{opt.name}</p>
                              <p className="text-slate-500 text-xs">{opt.description}</p>
                            </div>
                            {opt.price_avg && (
                              <span className="text-cyan-400 text-sm">+{opt.price_avg}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10 h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <Car className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <p className="text-slate-400">Wählen Sie ein Fahrzeug aus der Liste</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTuningData;
